import React from 'react';
import { Send, Database, FileText, LayoutGrid, TerminalSquare, FlaskConical } from 'lucide-react';

/**
 * Capability discovery for the AI panel's empty state (17.08.2026).
 *
 * The old empty state named three things — send requests, read collections,
 * manage variables — out of forty tools spanning six surfaces. Users could not
 * ask for what they had no way of knowing existed: nothing told them the
 * assistant can query their database, write a document, build a board, or run
 * a command over SSH. That is a discovery bug, not a capability gap.
 *
 * One card per SURFACE rather than per tool: the job is to communicate reach,
 * and forty entries would be a menu nobody reads. Each prompt is written the
 * way a user would actually type it, so clicking one also teaches the register
 * that works.
 */

/**
 * `prompt` is sent verbatim on click. Keep each one runnable against a fresh
 * workspace — a starter prompt that errors on first use teaches the opposite
 * of what it is here for, so these ask the assistant to LOOK before it acts.
 */
export const STARTER_PROMPTS = [
  {
    id: 'requests',
    icon: Send,
    surface: 'Requests',
    prompt: 'What collections do I have, and what is in them?',
  },
  {
    id: 'database',
    icon: Database,
    surface: 'Databases',
    prompt: 'Show me the schema of my database and explain how the tables relate.',
  },
  {
    id: 'documents',
    icon: FileText,
    surface: 'Documents',
    prompt: 'Write a document summarising what this workspace is set up to do.',
  },
  {
    id: 'boards',
    icon: LayoutGrid,
    surface: 'Task boards',
    prompt: 'Create a board to track the work in this workspace, with lists and a few tasks.',
  },
  {
    id: 'terminal',
    icon: TerminalSquare,
    surface: 'Terminal',
    prompt: 'Which servers can I reach over SSH?',
  },
  {
    id: 'testing',
    icon: FlaskConical,
    surface: 'Testing',
    prompt: 'Run my collection and explain anything that fails.',
  },
];

function AiStarterPrompts({ onPick }) {
  return (
    <div className="ai-starters" data-testid="ai-starter-prompts">
      {STARTER_PROMPTS.map(({ id, icon: Icon, surface, prompt }) => (
        <button
          key={id}
          type="button"
          className="ai-starter"
          onClick={() => onPick(prompt)}
          data-testid={`ai-starter-${id}`}
        >
          <Icon size={13} className="ai-starter__icon" />
          <span className="ai-starter__surface">{surface}</span>
          <span className="ai-starter__prompt">{prompt}</span>
        </button>
      ))}
    </div>
  );
}

export default AiStarterPrompts;
